import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { LockKey, Warning, Briefcase, Sparkle } from "@phosphor-icons/react";
import PasswordStrengthMeter from "../components/PasswordStrengthMeter";

/**
 * SetPassword — agent welcome page. Consumes a one-time set-password token
 * emailed to a newly-approved agent. Personalized with the agent's code + name
 * fetched from the verify endpoint (which never reveals email).
 *
 * On success: signs the agent in with a fresh JWT and routes them straight
 * to /agent so they can start working immediately.
 */
export default function SetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const t = useT();

  const [verifying, setVerifying] = useState(true);
  const [info, setInfo] = useState({ valid: false, agent_code: "", full_name: "", role: "" });
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [pwValid, setPwValid] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/auth/set-password/verify?token=${encodeURIComponent(token || "")}`)
      .then((r) => {
        if (!cancelled) setInfo(r.data || { valid: false });
      })
      .catch(() => {
        if (!cancelled) setInfo({ valid: false });
      })
      .finally(() => {
        if (!cancelled) setVerifying(false);
      });
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error(t("setpw.toast.too_short")); return; }
    if (pw !== pw2) { toast.error(t("setpw.toast.no_match")); return; }
    setLoading(true);
    try {
      const r = await api.post("/auth/set-password", { token, new_password: pw });
      if (!r.data?.token) {
        toast.success(t("setpw.toast.success_signin_again"));
        nav("/login?role=agent");
        return;
      }
      localStorage.setItem("cr_token", r.data.token);
      sessionStorage.removeItem("cr_token");
      setUser(r.data.user);
      toast.success(t("setpw.toast.welcome"));
      nav("/agent");
    } catch (err) {
      const msg = err?.response?.data?.detail || t("setpw.toast.expired");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-[#FBF9F6]">
        <Header />
        <div className="max-w-md mx-auto px-6 py-20 text-center text-[#4A5C50]" data-testid="set-password-verifying">
          {t("setpw.verifying")}
        </div>
      </div>
    );
  }

  if (!info.valid) {
    return (
      <div className="min-h-screen bg-[#FBF9F6]">
        <Header />
        <div className="max-w-md mx-auto px-6 py-20" data-testid="set-password-invalid">
          <div className="bg-white border border-rose-200 rounded-2xl p-8">
            <Warning size={36} weight="duotone" className="text-rose-600" />
            <h1 className="serif text-3xl mt-2">{t("setpw.invalid.title")}</h1>
            <p className="text-sm text-[#4A5C50] mt-3">{t("setpw.invalid.desc")}</p>
            <div className="mt-6 flex gap-3">
              <Link to="/"><Button variant="outline">{t("setpw.invalid.home_btn")}</Button></Link>
              <Link to="/support"><Button className="bg-[#184A30] text-white">{t("setpw.invalid.support_btn")}</Button></Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const firstName = (info.full_name || "").split(" ")[0] || t("setpw.fallback_name");
  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <Header />
      <div className="max-w-xl mx-auto px-6 py-14">
        <p className="text-xs tracking-[0.3em] uppercase text-[#4A5C50] flex items-center gap-2">
          <Briefcase size={14} weight="duotone" /> {t("setpw.eyebrow")}
        </p>
        <h1 className="serif text-5xl mt-2" data-testid="set-password-heading">
          {t("setpw.heading", { name: firstName })}
        </h1>
        <p className="text-[#4A5C50] mt-3">{t("setpw.subheading")}</p>

        {info.agent_code && (
          <div className="bg-white border border-[#E5E0D8] rounded-2xl p-5 mt-6 text-center" data-testid="set-password-code-card">
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#4A5C50]">{t("setpw.code_label")}</p>
            <p className="mono text-3xl font-semibold text-[#184A30] mt-1 tracking-widest">{info.agent_code}</p>
            <p className="text-[11px] text-[#4A5C50] mt-2 flex items-center justify-center gap-1">
              <Sparkle size={11} weight="duotone" /> {t("setpw.code_tag")}
            </p>
          </div>
        )}

        <form onSubmit={submit} className="bg-white border border-[#E5E0D8] rounded-2xl p-6 mt-6 space-y-4" data-testid="set-password-form">
          <h2 className="serif text-2xl">{t("setpw.form.title")}</h2>
          <div>
            <Label htmlFor="setpw">{t("setpw.form.new_label")}</Label>
            <div className="relative">
              <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5C50]" />
              <input
                id="setpw" type="password" required
                value={pw} onChange={(e) => setPw(e.target.value)}
                className="w-full pl-9 mt-1 rounded-lg border border-[#E5E0D8] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#184A30]"
                placeholder={t("setpw.form.placeholder")} minLength={8}
                data-testid="set-password-input"
              />
            </div>
            <PasswordStrengthMeter password={pw} onValidityChange={setPwValid} />
          </div>
          <div>
            <Label htmlFor="setpw2">{t("setpw.form.confirm_label")}</Label>
            <div className="relative">
              <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5C50]" />
              <input
                id="setpw2" type="password" required
                value={pw2} onChange={(e) => setPw2(e.target.value)}
                className="w-full pl-9 mt-1 rounded-lg border border-[#E5E0D8] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#184A30]"
                minLength={8}
                data-testid="set-password-confirm"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading || !pwValid || pw !== pw2}
            className="w-full bg-[#184A30] hover:bg-[#0F3320] text-white text-base py-5 rounded-xl"
            data-testid="set-password-submit"
          >
            {loading ? t("setpw.form.btn_busy") : t("setpw.form.btn_submit")}
          </Button>
        </form>
      </div>
    </div>
  );
}
